import { useQuery } from '@tanstack/react-query';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterInputs, type RouterOutputs } from '../../api/trpc';
import { isRunActive } from '../../lib/runs';

export type BuildStatus = RouterOutputs['performance']['status'];
export type CohortSummary = RouterOutputs['performance']['cohort'];
export type CohortInput = RouterInputs['performance']['cohort'];
export type CohortMetrics = CohortSummary['totals'];
export type ChannelSpend = CohortSummary['spend'];
export type StageMap = RouterOutputs['performance']['stageMap'];
export type MediaStatus = RouterOutputs['performance']['media']['status'];
export type CampaignMapList = RouterOutputs['performance']['mapping']['campaigns'];
export type CampaignMapRow = CampaignMapList['rows'][number];
export type CampaignMapInput = RouterInputs['performance']['mapping']['campaigns'];
export type ProjectMapList = RouterOutputs['performance']['mapping']['projects'];
export type ProjectMapRow = ProjectMapList['rows'][number];
export type ProjectMapInput = RouterInputs['performance']['mapping']['projects'];
export type MapFilter = NonNullable<CampaignMapInput['filter']>;
export type ChangelogRun = RouterOutputs['pipedrive']['changelog']['listRuns'][number];

/** Snapshot status; polls while a build runs or one is due. */
export function useBuildStatus() {
  return useQuery(
    trpc.performance.status.queryOptions(undefined, {
      refetchInterval: (query) => (query.state.data?.active || query.state.data?.stale ? 5000 : 60_000),
    }),
  );
}

export function useCohort(input: CohortInput, enabled: boolean) {
  return useQuery(trpc.performance.cohort.queryOptions(input, { enabled, staleTime: 30_000 }));
}

/** Media spend imports per platform; polls while an import runs. */
export function useMediaStatus() {
  return useQuery(
    trpc.performance.media.status.queryOptions(undefined, {
      refetchInterval: (query) => (query.state.data?.active ? 3000 : 60_000),
    }),
  );
}

/** Identity map: ad campaigns → CRM "Kampaň" options. */
export function useCampaignMap(input: CampaignMapInput) {
  return useQuery(trpc.performance.mapping.campaigns.queryOptions(input, { staleTime: 10_000 }));
}

/** CRM campaign options in use since the analytics start, for the picker (server-side search). */
export function useCampaignOptions(q: string, enabled: boolean) {
  return useQuery(trpc.performance.mapping.campaignOptions.queryOptions({ q }, { enabled, staleTime: 60_000 }));
}

/** Identity map: CRM "Poptaný projekt" options → Back Office projects. */
export function useProjectMap(input: ProjectMapInput) {
  return useQuery(trpc.performance.mapping.projects.queryOptions(input, { staleTime: 10_000 }));
}

export function useProjectTargets() {
  return useQuery(trpc.performance.mapping.projectTargets.queryOptions(undefined, { staleTime: 5 * 60_000 }));
}

export function invalidateMapping(): Promise<void> {
  return invalidateAll(trpc.performance.mapping.pathFilter(), trpc.performance.cohort.pathFilter());
}

export function useStageMap() {
  return useQuery(trpc.performance.stageMap.queryOptions(undefined, { staleTime: 5 * 60_000 }));
}

/** Deal-history loads; polls while one is queued or running. */
export function useChangelogRuns() {
  return useQuery(
    trpc.pipedrive.changelog.listRuns.queryOptions(undefined, {
      refetchInterval: (query) => (query.state.data?.some((r) => isRunActive(r.status)) ? 3000 : false),
    }),
  );
}

export function useHistoryCoverage(pipelineIds: number[], since: string) {
  return useQuery(
    trpc.pipedrive.changelog.coverage.queryOptions({ pipelineIds, since }, { enabled: pipelineIds.length > 0 }),
  );
}

export function invalidatePerformance(): Promise<void> {
  return invalidateAll(trpc.performance.pathFilter(), trpc.pipedrive.changelog.pathFilter());
}
